/**
 * Tree logic for indicators: build hierarchy from flat IDs, detect gaps, orphans, duplicates.
 * IDs are dot-separated numbers, e.g. 4, 4.1, 4.2, 4.2.1, 4.2.2.
 */

import { AdminIndicatorRow } from '../services/api';

export function getParentId(id: string): string | null {
    const parts = id.split('.');
    if (parts.length <= 1) return null;
    parts.pop();
    return parts.join('.');
}

export interface TreeIndicatorNode {
    id: string;
    indicator: AdminIndicatorRow;
    children: TreeIndicatorNode[];
    /** Sibling index 1-based (e.g. 4.2.1 is third child of 4.2) */
    siblingIndex?: number;
    /** Parent id if any */
    parentId: string | null;
}

/** Build tree from flat list. Orphans (parent missing) become roots. */
export function buildIndicatorTree(indicators: AdminIndicatorRow[]): TreeIndicatorNode[] {
    const byId = new Map<string, TreeIndicatorNode>();
    indicators.forEach((ind) => {
        byId.set(ind.id, {
            id: ind.id,
            indicator: ind,
            children: [],
            parentId: getParentId(ind.id),
        });
    });

    const roots: TreeIndicatorNode[] = [];
    byId.forEach((node) => {
        const parent = node.parentId ? byId.get(node.parentId) : null;
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    // Sort children by id (numeric)
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    function sortAndNumber(nodes: TreeIndicatorNode[], startIndex = 1): void {
        nodes.sort((a, b) => collator.compare(a.id, b.id));
        nodes.forEach((n, i) => {
            n.siblingIndex = startIndex + i;
            sortAndNumber(n.children, 1);
        });
    }
    sortAndNumber(roots);

    return roots;
}

/** IDs that are missing between siblings (e.g. have 4.2.1 and 4.2.3 → 4.2.2) */
export function detectNumberingGaps(indicators: AdminIndicatorRow[]): string[] {
    const tree = buildIndicatorTree(indicators);
    const gaps: string[] = [];

    function checkSiblings(parentId: string | null, children: TreeIndicatorNode[]): void {
        if (children.length <= 1) return;
        const prefix = parentId ? `${parentId}.` : '';
        const indices = children.map((c) => {
            const lastPart = c.id.split('.').pop() || '';
            const n = parseInt(lastPart, 10);
            return isNaN(n) ? 0 : n;
        });
        const min = Math.min(...indices);
        const max = Math.max(...indices);
        for (let i = min; i <= max; i++) {
            const expectedId = prefix ? `${prefix}${i}` : String(i);
            if (!children.some((c) => c.id === expectedId)) {
                gaps.push(expectedId);
            }
        }
    }

    function walk(parentId: string | null, nodes: TreeIndicatorNode[]): void {
        checkSiblings(parentId, nodes);
        nodes.forEach((n) => walk(n.id, n.children));
    }
    walk(null, tree);

    return gaps;
}

/** Gap ids that are direct children of this parent (for tree placeholder). */
export function getGapsForParent(parentId: string | null, gapIds: string[]): string[] {
    return gapIds.filter((id) => getParentId(id) === parentId);
}

/** Indicator ids whose parent is not in the list (orphans). */
export function detectOrphans(indicators: AdminIndicatorRow[]): string[] {
    const ids = new Set(indicators.map((i) => i.id));
    const orphans: string[] = [];
    indicators.forEach((ind) => {
        const parentId = getParentId(ind.id);
        if (parentId && !ids.has(parentId)) {
            orphans.push(ind.id);
        }
    });
    return orphans;
}

/** Duplicate ids in the list. */
export function detectDuplicates(indicators: AdminIndicatorRow[]): string[] {
    const seen = new Map<string, number>();
    indicators.forEach((ind) => {
        seen.set(ind.id, (seen.get(ind.id) ?? 0) + 1);
    });
    return [...seen.entries()].filter(([, count]) => count > 1).map(([id]) => id);
}

/** Suggested next id under a parent (e.g. parent "4.2" → "4.2.3" if 4.2.1 and 4.2.2 exist). */
export function suggestNextId(indicators: AdminIndicatorRow[], parentId: string | null): string {
    const prefix = parentId ? `${parentId}.` : '';
    const siblings = indicators.filter((ind) => getParentId(ind.id) === parentId);
    if (siblings.length === 0) return prefix ? `${prefix}1` : '1';
    const lastParts = siblings.map((ind) => {
        const last = ind.id.split('.').pop() || '';
        const n = parseInt(last, 10);
        return isNaN(n) ? 0 : n;
    });
    const next = Math.max(0, ...lastParts) + 1;
    return prefix ? `${prefix}${next}` : String(next);
}

/** All descendant ids of a node (for "trash with children"). */
export function getDescendantIds(indicators: AdminIndicatorRow[], parentId: string): string[] {
    const descendants: string[] = [];
    indicators.forEach((ind) => {
        let cur: string | null = ind.id;
        while (cur) {
            const p = getParentId(cur);
            if (p === parentId) {
                descendants.push(ind.id);
                break;
            }
            cur = p;
        }
    });
    return descendants;
}

/** Children of a node (direct only). */
export function getChildIds(indicators: AdminIndicatorRow[], parentId: string): string[] {
    return indicators.filter((ind) => getParentId(ind.id) === parentId).map((ind) => ind.id);
}
